// Protección de datos en el panel (backend/privacy): plazos de conservación, resumen de la purga diaria,
// textos del borrado de una reserva, descargas de exportaciones y registro de accesos.
import { formatError } from "./api";
import { fecha, fechaHora } from "./format";

export const PLAZOS = [
  { campo: "doc_photos_days", titulo: "Fotos de documentos y ubicación", unidad: "días después de liquidar la fianza", min: 1, max: 3650 },
  { campo: "vehicle_photos_days", titulo: "Fotos del vehículo", unidad: "días después de liquidar la fianza", min: 1, max: 3650 },
  { campo: "unconfirmed_days", titulo: "Reservas que no llegan a confirmarse", unidad: "días desde la anulación o la caducidad (o el alta, si sigue sin pagar)", min: 7, max: 3650 },
  { campo: "reservation_years", titulo: "Reserva entera", unidad: "años desde la devolución", min: 1, max: 20 },
];

export function avisoPlazos(ajustes) {
  if (!ajustes) return null;
  if (!ajustes.enabled) {
    return "La supresión automática está apagada: los datos se guardan sin plazo y la política de privacidad lo dice así.";
  }
  if (Number(ajustes.reservation_years) < 6) {
    return "El Código de Comercio obliga a conservar la documentación contable 6 años: con menos, consúltalo antes con tu asesor.";
  }
  return null;
}

// «1 reserva borrada», «3 reservas borradas».
const reservas = (n, adjetivo) => (n === 1 ? `1 reserva${adjetivo ? ` ${adjetivo}` : ""}` : `${n} reservas${adjetivo ? ` ${adjetivo}s` : ""}`);

// «a», «a y b», «a, b y c» (o con «ni» tras una negación).
const enumerar = (partes, y = "y") => (partes.length > 1 ? `${partes.slice(0, -1).join(", ")} ${y} ${partes[partes.length - 1]}` : partes[0]);

// Pasos de la purga diaria (backend/privacy/daily.py) tal como los apunta en `fallos` («paso: TipoDeError»).
const PASOS = {
  fotos: "la supresión de las fotos",
  "reservas enteras": "el borrado de las reservas",
  "retenciones vencidas": "el borrado de lo que ya no hay obligación de conservar",
  registro: "la supresión de los datos del registro de viajeros",
  "pagos huérfanos": "el borrado de los pagos de reservas ya borradas",
  ficheros: "el borrado de los ficheros",
  pasada: "la purga (se interrumpió)",
};

// Con unos ajustes guardados que no validan, la purga diaria no suprime nada (`ajustes_no_validos`).
export const SIN_SUPRIMIR = "No se ha suprimido nada: los plazos guardados no son válidos; revísalos en Protección de datos.";

// Solo lo que hizo la purga, contado en reservas; «a, b y c». Un paso que falla entero suma uno a `errores` y va en
// `fallos`: se nombra aparte y no cuenta como reserva.
export function resumenPurga(r) {
  if (!r) return "Todavía no se ha hecho ninguna purga.";
  if (Array.isArray(r.ajustes_no_validos) && r.ajustes_no_validos.length) return `${fecha(r.at)}. ${SIN_SUPRIMIR}`;
  if (r.enabled === false) return `${fecha(r.at)}: la supresión automática estaba apagada.`;
  const fallos = Array.isArray(r.fallos) ? r.fallos : [];
  const conErrores = Math.max(0, (r.errores || 0) - fallos.length);
  const borradas = (r.sin_confirmar || 0) + (r.antiguas || 0);
  const partes = [
    r.documentos && `${reservas(r.documentos)} sin las fotos de documentos`,
    r.vehiculo && `${reservas(r.vehiculo)} sin las fotos del vehículo`,
    borradas && reservas(borradas, "borrada"),
    r.registro && `${reservas(r.registro)} sin los datos del registro de viajeros`,
    r.retenidas && `${reservas(r.retenidas, "conservada")} por obligación legal`,
    conErrores && `${reservas(conErrores)} con errores; se reintentará${conErrores === 1 ? "" : "n"}`,
  ].filter(Boolean);
  const pasos = [...new Set(fallos.map((f) => String(f).split(":")[0].trim()))].map((p) => PASOS[p] || p);
  const fallo = pasos.length ? `no se pudo terminar ${enumerar(pasos, "ni")}; se reintentará.` : "";
  // Ficheros ya suprimidos que no se pudieron borrar del disco (los reintenta el arranque): no van en `errores`.
  const n = Number(r.ficheros_con_error) || 0;
  const ficheros = n ? `${n === 1 ? "1 fichero no se pudo" : `${n} ficheros no se pudieron`} borrar del disco; se reintentará.` : "";
  const frases = [fallo, ficheros].filter(Boolean);
  const primera = partes.length ? `${enumerar(partes)}.` : frases.shift() || "no había nada que suprimir.";
  return [`${fecha(r.at)}: ${primera}`, ...frases.map((f) => `${f[0].toUpperCase()}${f.slice(1)}`)].join(" ");
}

// Nombre en pantalla de los ajustes que no son plazos.
const AJUSTES = { enabled: "Supresión automática", export_third_party: "Datos de la otra persona al exportar" };

// Si lo guardado no vale para la purga, los ajustes por corregir por su nombre en pantalla; `null` si vale. Lo decide
// el backend (`stored_valid` de /admin/privacy/settings, con las reglas de la purga): el panel no las repite. Los
// nombres son los que la última purga dio por no válidos (vacío si todavía no lo ha dicho).
export function ajustesPorCorregir(guardados) {
  if (guardados?.stored_valid !== false) return null;
  const malos = guardados.last_run?.ajustes_no_validos;
  return (Array.isArray(malos) ? malos : [])
    .map((campo) => AJUSTES[campo] || PLAZOS.find((p) => p.campo === campo)?.titulo || campo);
}

export const TEXTO_BORRADO =
  "Se borran la reserva, sus fotos y documentos, sus correos y sus pagos. El libro de caja conserva los importes " +
  "sin el nombre del cliente. Las copias de seguridad lo guardan 14 días más. El cliente y su tarjeta en Stripe " +
  "se gestionan desde tu panel de Stripe. No se puede deshacer.";

const LIBRO = {
  anonimizado: "el libro de caja queda sin el nombre del cliente",
  borrado: "no había movimientos de dinero en el libro de caja",
  "sin apuntes": "sin apuntes en el libro de caja",
};

// «1 fichero», «3 ficheros».
export const cuantos = (n, palabra) => `${n} ${palabra}${n === 1 ? "" : "s"}`;

export function resumenBorrado(d) {
  if (!d) return "Reserva eliminada";
  const libro = LIBRO[d.libro];
  return `Reserva eliminada: ${cuantos(d.ficheros || 0, "fichero")}, ${cuantos(d.correos || 0, "correo")} y ${cuantos(d.pagos || 0, "pago")}${libro ? `; ${libro}` : ""}.`;
}

export function nombreDescarga(cabecera, porDefecto = "datos.zip") {
  const m = /filename="?([^";]+)"?/i.exec(cabecera || "");
  return m ? m[1] : porDefecto;
}

export function descargarBlob(resp, porDefecto) {
  const url = URL.createObjectURL(resp.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = nombreDescarga(resp.headers?.["content-disposition"], porDefecto);
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Con responseType «blob», axios entrega el error de FastAPI como un Blob: se lee y su detalle (texto, la lista de
// un 422 o un objeto con `message`) pasa por `formatError`, que siempre da texto (un objeto en el toast rompe React).
export async function errorDeBlob(err) {
  const data = err?.response?.data;
  if (!(data instanceof Blob)) return null;
  try {
    const cuerpo = JSON.parse(await data.text());
    return cuerpo?.detail ? formatError({ response: { data: cuerpo } }) : null;
  } catch {
    return null;
  }
}

// Registro de accesos a datos personales (backend/privacy/accesslog.py): cada acción, como la lee quien la revisa.
export const ACCIONES = {
  ver_listado: "ha visto el listado de reservas",
  ver_reserva: "ha visto la reserva",
  descargar_contrato: "ha descargado el contrato",
  cambiar_correo: "ha cambiado el correo del cliente",
  suprimir_fotos: "ha suprimido fotos",
  exportar_cliente: "ha exportado los datos del cliente",
  borrar_reserva: "ha borrado la reserva",
  exportar_libro: "ha exportado el libro de caja",
  ver_libro: "ha visto el libro de caja",
  ver_calendario: "ha visto el calendario de reservas",
  ver_registro: "ha visto los datos del registro de viajeros",
  cambiar_viajeros: "ha cambiado los datos de los viajeros",
  ver_comunicacion: "ha visto una comunicación al registro de viajeros",
  descargar_xml_registro: "ha descargado el XML para SES",
  presentar_registro: "ha marcado presentada en SES una comunicación",
  registro_a_mano: "ha marcado comunicada a mano una comunicación",
  reintentar_registro: "ha reintentado una comunicación",
};

// «14/10/2026 11:30 · ana@empresa.example · ha visto la reserva», con la hora de quien mira (`timeZone` solo lo
// fijan los tests); una acción nueva sin texto sale con su nombre.
export function lineaAcceso(f, timeZone) {
  return `${fechaHora(f.at, timeZone)} · ${f.user_email} · ${ACCIONES[f.accion] || f.accion}`;
}
